// ── usePolymarketRelayPositions ────────────────────────────────
//
// Auto-polling React hook backed by /api/polymarket/positions. Returns
// the trader's derived open positions (netCount, avgEntry, runningRealized)
// computed server-side from the persisted fills ledger.
//
// Pairs with usePolymarketRelayPnl for the open notional / realized
// summary line. Same unavailable-state surfacing pattern as the other
// poly relay hooks so consumers can render one shared error UI.
//
// Unavailable is SELF-HEALING, not one-shot (the usePolymarketRelayOrders
// idiom): while the relay reports a coded 404 (NOT_IN_ORG /
// PERSONAL_KEY_MODE / NO_POLY_CRED / ONLY_LEGACY_EOA_CRED) polling slows
// to a re-probe every UNAVAILABLE_REPROBE_TICKS-th tick instead of
// stopping, so an admin re-adding the credential heals every mount
// without a manual refresh. It previously stopped dead: consumers with no
// Refresh control (the ValueDashboard empty-tennis filter) stayed blind
// until remount, and a blind read is not the same as "no position".
//
// State is also keyed to the authenticated identity: an org switch keeps
// this hook mounted, and the PREVIOUS account's positions must never be
// read as evidence about the next one. On an identity change the hook
// clears its rows and re-arms the fetch from `idle`.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getPolyPnlViaRelay,
  listPolyPositionsViaRelay,
  type PolyRelayPnl,
  type PolyRelayPosition,
  type PolyRelayUnavailableCode,
  type PolyReadCredentialContext,
} from '@/lib/polymarket/relay';
import { useAuthStore } from '@/stores/authStore';
import { useAccountStore } from '@/stores/accountStore';
import { polyAccountReadScope } from '@/lib/polymarket/accountScope';
import { startVisibilityAwareInterval } from '@/lib/visibilityThrottle';

export type PolyRelayPositionsStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'error'
  | 'unavailable';

export interface PolyRelayPositionsState {
  positions: PolyRelayPosition[];
  pnl: PolyRelayPnl | null;
  status: PolyRelayPositionsStatus;
  /** Latest completed read confirms the displayed rows. Separate from the
   * display status, which deliberately retains rows through brief blips. */
  snapshotConfirmed: boolean;
  error: string | null;
  unavailableCode: PolyRelayUnavailableCode | null;
  /** Advisory only: historical ledger reads remain ready while this explains
   * whether the caller currently has a usable org trading credential. */
  credentialContext: PolyReadCredentialContext;
  lastFetchMs: number | null;
}

export interface UsePolymarketRelayPositionsOptions {
  enabled?: boolean;
  pollMs?: number;
  /** When true, also fetches /pnl on each tick. Defaults to true; pass
   *  false for views that only need open positions (no realized/open
   *  notional summary). */
  includePnl?: boolean;
  /** #1068 — start a new mount from the account's last confirmed reading
   *  (when recent) instead of from nothing. For a surface that is remounted
   *  as the trader moves between markets, so a holding does not drop to
   *  "loading" or vanish on the first slow or failed read after the move.
   *  The seeded state is never `snapshotConfirmed`; see `seedFromLastReading`. */
  seedFromLastReading?: boolean;
}

export interface UsePolymarketRelayPositionsReturn extends PolyRelayPositionsState {
  refresh: () => Promise<void>;
}

/** Hook state plus the account identity it was fetched under. Never returned:
 *  `identity` and `seeded` are stripped before the hook returns its state. */
interface StampedPositionsState extends PolyRelayPositionsState {
  identity: string;
  /** #1068: these rows came from another mount's reading, not this mount's.
   *  Never certified, and never "rows already on screen" for the empty-confirm
   *  or hold-steady debounces (both check it). */
  seeded?: true;
}

const IDLE_POSITIONS_STATE: PolyRelayPositionsState = {
  positions: [],
  pnl: null,
  status: 'idle',
  snapshotConfirmed: false,
  error: null,
  unavailableCode: null,
  credentialContext: null,
  lastFetchMs: null,
};

const DEFAULT_POLL_MS = 10_000;

// ── #1068: last confirmed reading, shared across mounts ─────────
// Game Detail remounts its order panel whenever the selected market's
// identity changes (detailed spread/total rows, every period and prop line).
// Each mount of this hook used to start idle, so returning to a market showed
// "Position data loading…" — or, on a failed first read, no Polymarket holding
// at all — where the previous mount had been showing it a moment earlier.
//
// Only a CONFIRMED ready read is recorded, stamped with the account identity
// that fetched it. A mount that opts in starts from it as `loading` — never
// `ready`, never `snapshotConfirmed` — so the reading is shown as a previous
// one (the position card says "loading — showing the last reading") for as
// long as this mount's own first read takes, even one that never completes.
// It is marked `seeded`, so it never counts as rows this mount has shown: the
// first answer replaces it outright (an empty read clears it, a coded 404
// flips to unavailable) instead of being held by the #424 debounces. The age
// bound keeps a long-departed reading from being offered at all.
const SEED_MAX_AGE_MS = 3 * DEFAULT_POLL_MS;
let seedGeneration = 0;
let readingSequence = 0;
let _lastConfirmed: {
  identity: string;
  sequence: number;
  at: number;
  positions: PolyRelayPosition[];
  credentialContext: PolyReadCredentialContext;
  lastFetchMs: number | null;
} | null = null;

function seedFromLastReading(identity: string): Omit<StampedPositionsState, 'identity'> {
  const last = _lastConfirmed;
  if (!last || last.identity !== identity || Date.now() - last.at > SEED_MAX_AGE_MS) {
    return IDLE_POSITIONS_STATE;
  }
  return {
    ...IDLE_POSITIONS_STATE,
    positions: last.positions,
    status: 'loading',
    snapshotConfirmed: false,
    seeded: true,
    credentialContext: last.credentialContext,
    lastFetchMs: last.lastFetchMs,
  };
}

/** Registered in authStore's purgeUserCaches: the previous account's
 *  holdings must never seed the next session. The identity stamp already
 *  refuses them; this also frees the rows. */
export function resetPolyPositionsSeed(): void {
  seedGeneration += 1;
  _lastConfirmed = null;
}
// While unavailable, re-probe every Nth poll tick (60s at 10s polls) instead
// of stopping — self-heals after an admin re-adds the cred. Matches the
// orders hook's UNAVAILABLE_REPROBE_TICKS so the two surfaces recover on a
// comparable horizon; positions has no module-shared fetch (the #1068 seed above
// only starts a mount, it never answers a read), but concurrent probes are
// still guarded by inFlightRef so the traffic stays bounded.
const UNAVAILABLE_REPROBE_TICKS = 6;
// A coded 404 can be TRANSIENT (credential edit: resolvePolyContext throws
// NoPolyCredentialsError from a live DB read before clientCache.set) — the
// same reality the orders hook debounces. A mount already showing positions
// holds status+rows until the answer proves persistent: this many CONSECUTIVE
// coded results (~30s at 10s polls). A mount with no data flips immediately.
const UNAVAILABLE_FLIP_STREAK = 3;
// A well-formed successful empty can still be a transient account-projection
// snapshot. Starting from visible rows, require two consecutive empty reads so
// one blip cannot erase the Position Tracker; a genuinely flat account clears
// on the next normal poll (about 10s with the default cadence).
const EMPTY_CONFIRM_STREAK = 2;

export function usePolymarketRelayPositions(
  options: UsePolymarketRelayPositionsOptions = {},
): UsePolymarketRelayPositionsReturn {
  const enabled = options.enabled ?? true;
  const pollMs = options.pollMs ?? DEFAULT_POLL_MS;
  const includePnl = options.includePnl ?? true;
  const seed = options.seedFromLastReading ?? false;

  // Account identity. Logout unmounts every authenticated surface, but an ORG
  // SWITCH mutates user.orgId in place with this hook still mounted — and the
  // relay resolves positions per (user, org). Selecting the composed string
  // keeps the zustand subscription firing only on a real identity change.
  useAccountStore(polyAccountReadScope);
  const identity = useAuthStore(polyAccountReadScope);

  // State is stamped with the identity it was fetched under and DERIVED back
  // out below, rather than cleared by an effect on identity change. That makes
  // cross-account leakage structurally impossible instead of merely handled:
  // there is no ordering in which the previous account's rows can be returned,
  // and a response still on the wire during an org switch lands stamped with
  // the old identity and is simply never selected. (The effect-clears-state
  // version also tripped the React Compiler lint on both a render-phase ref
  // write and a synchronous setState inside an effect.)
  const [stamped, setStamped] = useState<StampedPositionsState>(() => ({
    ...(seed ? seedFromLastReading(identity) : IDLE_POSITIONS_STATE),
    identity,
  }));
  const current: PolyRelayPositionsState =
    stamped.identity === identity ? stamped : IDLE_POSITIONS_STATE;

  // The IDENTITY that owns the in-flight request, not a bare boolean
  // (Danielle R2 DNL-R1-001 / P1, feed-filters-per-view): on an org switch the
  // new identity's first read must start immediately even while the previous
  // identity's request is still on the wire — that request is stamp-fenced
  // (it can never populate the new identity) and it may never complete at
  // all (a hung relay), so it must not hold the latch against the account
  // that is actually mounted. Same-identity requests still coalesce.
  const inFlightRef = useRef<string | null>(null);
  // Per-identity request generation: with two identities able to overlap, a
  // switch A→B→A can leave two A requests in flight; only the LATEST started
  // one for an identity may apply (an older completion is dropped) and only
  // it releases the latch. Cross-identity results are handled by the stamp
  // fence exactly as before.
  const requestGenRef = useRef(0);
  const latestGenByIdentityRef = useRef(new Map<string, number>());
  // A panel can unmount while its request is still on the wire. That result
  // must not write the shared remount seed. A generation also fences Strict
  // Mode's cleanup/setup cycle, where the same ref becomes mounted again.
  const lifecycleRef = useRef({ mounted: false, generation: 0 });
  useEffect(() => {
    const lifecycle = lifecycleRef.current;
    lifecycle.mounted = true;
    lifecycle.generation += 1;
    return () => {
      lifecycle.mounted = false;
      lifecycle.generation += 1;
      inFlightRef.current = null;
    };
  }, []);
  // The identity this mount is CURRENTLY rendering, readable from an async
  // callback. With two identities able to overlap, a request that outlived
  // its account must not WRITE at all: the stamp fence would render its
  // result as idle, which is safe for the old rows but wipes the new
  // account's already-applied state until its next poll. Written in an
  // effect, never during render.
  const currentIdentityRef = useRef(identity);
  useEffect(() => { currentIdentityRef.current = identity; }, [identity]);
  // Synchronous mirror of the committed state. The poll tick reads `.status`
  // from it so the interval never needs re-creating on a status change (the old
  // code achieved that with a setStatus-closure read, which cannot also express
  // a tick counter), and the error branch reads `.positions` from it to preserve
  // last-known rows without putting state in `refresh`'s dependency list.
  // Written only from async callbacks, never during render.
  const stateRef = useRef<StampedPositionsState>(stamped);
  // Poll ticks observed while unavailable — every UNAVAILABLE_REPROBE_TICKS-th
  // one re-probes.
  const unavailableTicksRef = useRef(0);
  // Consecutive coded-404 results; any other outcome resets it.
  const unavailableStreakRef = useRef(0);
  // Consecutive successful 200/[] results while this identity has visible rows.
  const emptyStreakRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!lifecycleRef.current.mounted) return;
    if (inFlightRef.current === identity) return;
    const lifecycleGeneration = lifecycleRef.current.generation;
    const cacheGeneration = seedGeneration;
    const sequence = ++readingSequence;
    const myGen = ++requestGenRef.current;
    latestGenByIdentityRef.current.set(identity, myGen);
    inFlightRef.current = identity;
    const superseded = (): boolean =>
      !lifecycleRef.current.mounted
      || lifecycleRef.current.generation !== lifecycleGeneration
      || latestGenByIdentityRef.current.get(identity) !== myGen;
    // Anything carried forward must come from THIS identity's state; a stamp
    // from a previous account degrades to the idle baseline instead.
    const carry = (): Omit<StampedPositionsState, 'identity'> =>
      stateRef.current.identity === identity ? stateRef.current : IDLE_POSITIONS_STATE;
    const apply = (next: PolyRelayPositionsState) => {
      // A result for an identity this mount no longer renders is dropped;
      // the stamp below stays as defense in depth for anything that slips.
      if (superseded() || currentIdentityRef.current !== identity) return;
      const resolved: StampedPositionsState = { ...next, identity };
      stateRef.current = resolved;
      setStamped(resolved);
      // A purge fences reads already in flight. Among concurrent mounted
      // readers, an earlier-started request cannot replace a newer reading.
      if (next.status === 'ready' && next.snapshotConfirmed
        && cacheGeneration === seedGeneration
        && (!_lastConfirmed || sequence > _lastConfirmed.sequence)) {
        _lastConfirmed = {
          identity,
          sequence,
          at: Date.now(),
          positions: next.positions,
          credentialContext: next.credentialContext,
          lastFetchMs: next.lastFetchMs,
        };
      }
    };
    if (carry().status === 'idle') apply({ ...carry(), status: 'loading' });
    try {
      const [posOut, pnlOut] = await Promise.all([
        listPolyPositionsViaRelay(),
        includePnl ? getPolyPnlViaRelay() : Promise.resolve(null),
      ]);
      // Drop BEFORE any evidence is touched (Danielle R3, DNL-R1-001 / P1):
      // a completion for an identity this mount no longer renders, or one a
      // newer same-identity request has superseded, must not cast a vote in
      // the unavailable / empty streaks, advance the poll counters, or write
      // state. The streaks are reset on every identity change (the polling
      // effect below), so with this drop no stale identity can reach them.
      if (superseded() || currentIdentityRef.current !== identity) return;
      const lastFetchMs = Date.now();
      if (posOut.ok) {
        unavailableTicksRef.current = 0;
        unavailableStreakRef.current = 0;
        const visible = carry();
        // Seeded rows are another mount's reading: an empty answer is this
        // mount's first evidence, not a blip against rows it has shown (#1068).
        const confirmingVisibleEmpty =
          posOut.positions.length === 0 && visible.positions.length > 0 && !visible.seeded;
        emptyStreakRef.current = confirmingVisibleEmpty
          ? emptyStreakRef.current + 1
          : 0;
        if (confirmingVisibleEmpty && emptyStreakRef.current < EMPTY_CONFIRM_STREAK) {
          // Keep positions and their matching P&L together until the empty is
          // confirmed; applying the new P&L beside retained rows would create
          // a mixed-generation account view.
          apply({
            ...visible,
            status: 'ready',
            snapshotConfirmed: false,
            error: null,
            unavailableCode: null,
            lastFetchMs,
          });
          return;
        }
        apply({
          positions: posOut.positions,
          // Pnl is best-effort; positions ok → still render. Don't flip the
          // whole state to error just because pnl failed.
          pnl: pnlOut && pnlOut.ok ? pnlOut.pnl : null,
          status: 'ready',
          snapshotConfirmed: true,
          error: null,
          unavailableCode: null,
          credentialContext:
            posOut.credentialContext !== undefined
              ? posOut.credentialContext
              : pnlOut && pnlOut.ok && pnlOut.credentialContext !== undefined
                ? pnlOut.credentialContext
                : 'CONTEXT_CHECK_FAILED',
          lastFetchMs,
        });
      } else if (posOut.accountingConflict) {
        // Known contradictory ledger evidence invalidates the prior snapshot.
        // Retaining its quantities would leave stale Close targets actionable.
        resetPolyPositionsSeed();
        emptyStreakRef.current = 0;
        unavailableStreakRef.current = 0;
        apply({ positions: [], pnl: null, status: 'error', snapshotConfirmed: false,
          error: posOut.error, unavailableCode: null, credentialContext: carry().credentialContext, lastFetchMs });
      } else if (posOut.code) {
        emptyStreakRef.current = 0;
        // Debounced unavailable — the usePolymarketRelayOrders idiom. The
        // debounce reads only current-identity state (carry()), so an org
        // switch never inherits the previous account's streak or rows.
        const visible = carry();
        unavailableStreakRef.current += 1;
        const holdSteady =
          !visible.seeded &&
          visible.status === 'ready' &&
          visible.positions.length > 0 &&
          unavailableStreakRef.current < UNAVAILABLE_FLIP_STREAK;
        if (holdSteady) {
          // Preserve display stability without certifying a failed read.
          apply({ ...visible, snapshotConfirmed: false, lastFetchMs });
        } else {
          apply({
            positions: [],
            pnl: null,
            status: 'unavailable',
            snapshotConfirmed: false,
            error: posOut.error,
            unavailableCode: posOut.code,
            credentialContext: posOut.code,
            lastFetchMs,
          });
        }
      } else {
        unavailableTicksRef.current = 0;
        unavailableStreakRef.current = 0;
        emptyStreakRef.current = 0;
        // A transport/5xx failure leaves last-known rows standing rather than blanking
        // PositionTracker on a transient blip. Safe for the #192 filter because
        // its readiness gate keys off `status`, not off emptiness — 'error' is
        // never authoritative, so stale rows can't justify hiding anything.
        apply({
          ...(posOut.status === 401 || posOut.status === 403 ? IDLE_POSITIONS_STATE : carry()),
          status: 'error',
          snapshotConfirmed: false,
          error: posOut.error,
          unavailableCode: null,
          lastFetchMs,
        });
      }
    } finally {
      // Release only what this request owns: not another identity's latch,
      // and not a newer same-identity request's.
      if (inFlightRef.current === identity && !superseded()) inFlightRef.current = null;
    }
  }, [includePnl, identity]);

  // Initial fetch + polling lifecycle. Polling slows to 1-in-
  // UNAVAILABLE_REPROBE_TICKS while the relay says "unavailable" — the hook
  // self-heals when the cred comes back; manual refresh still works.
  // `identity` is not listed: it is already a dependency of `refresh`, so an
  // org switch recreates that callback and re-runs this effect — tearing the old
  // timer down and firing an immediate fetch under the new identity.
  useEffect(() => {
    if (!enabled) return;
    let stopped = false;
    // A carried-over unavailable streak from the previous identity must not
    // throttle this one's first ticks. Effect body, so not a render-phase write.
    unavailableTicksRef.current = 0;
    unavailableStreakRef.current = 0;
    emptyStreakRef.current = 0;
    void refresh();
    const tick = () => {
      if (stopped) return;
      if (stateRef.current.status === 'unavailable') {
        unavailableTicksRef.current += 1;
        if (unavailableTicksRef.current % UNAVAILABLE_REPROBE_TICKS !== 0) return;
      } else {
        unavailableTicksRef.current = 0;
      }
      void refresh();
    };
    // 1/6 poll rate while the tab is hidden, immediate tick on refocus.
    const stopInterval = startVisibilityAwareInterval(tick, pollMs);
    return () => {
      stopped = true;
      stopInterval();
    };
  }, [enabled, pollMs, refresh]);

  // Strip the internal stamp and seed mark (#1068): consumers see only state.
  const { identity: _identity, seeded: _seeded, ...state } = current as StampedPositionsState;
  return { ...state, refresh };
}
